import { Button, Stack, SxProps, Theme, Typography } from '@mui/material';
import { useCallback } from 'react';
import { OverflowList } from 'src/components';
import { Conversation, PreparedConversationParticipantOption } from 'src/graphql';
import { ParticipantSelectorItem } from 'src/pages/Conversations/Chat/ParticipationPanel/types';
import { Modals, useShowModals } from 'src/store/modals';
import { ParticipantChipItem } from './participant-chip-item';

interface ParticipantChipListProps {
  title: string;
  conversation: Conversation;
  participants: ParticipantSelectorItem[];
  showAddButton?: boolean;
  addParticipantLabel?: string;
  onAddParticipant: (data: PreparedConversationParticipantOption | string) => void;
  onRemoveParticipant?: (item: ParticipantSelectorItem | null) => void;
  onAddEmail: (data: ParticipantSelectorItem, email: string) => void;
  onRemoveEmail: (data: ParticipantSelectorItem, email: string) => void;
  displayInOneLine?: boolean;
  readOnly?: boolean;
  sx?: SxProps<Theme>;
  endAdornment?: React.ReactNode | null;
}

export const ParticipantChipList = (props: ParticipantChipListProps) => {
  const { displayInOneLine = false, readOnly, showAddButton = true, sx, endAdornment } = props;

  const [showUserSearchModal] = useShowModals(Modals.UserSearchModal);

  const openAddParticipantModal = useCallback(() => {
    showUserSearchModal({
      partnerId: props.conversation.partnerId,
      onSelect: (participant: PreparedConversationParticipantOption | string) => {
        props.onAddParticipant(participant);
      },
    });
  }, [props, showUserSearchModal]);

  const renderShowMoreElement = useCallback((hiddenCount: number) => {
    return (
      <Typography variant="body1" sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
        + {hiddenCount} more
      </Typography>
    );
  }, []);

  const renderParticipantElement = useCallback(
    (participant: ParticipantSelectorItem) => {
      return (
        <ParticipantChipItem
          readOnly={readOnly}
          key={participant.participantIdentity}
          participant={participant}
          onAddEmail={props.onAddEmail}
          onRemoveEmail={props.onRemoveEmail}
          onDelete={
            props.onRemoveParticipant
              ? () => props.onRemoveParticipant!(participant)
              : undefined
          }
        />
      );
    },
    [props.onAddEmail, props.onRemoveEmail, props.onRemoveParticipant, readOnly],
  );

  return (
    <Stack
      sx={{
        minWidth: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: '0.3rem',
        padding: '0.2rem 0.4rem',
        flexWrap: displayInOneLine ? 'nowrap' : 'wrap',
        flexShrink: displayInOneLine ? 1 : 0,
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          gap: '0.3rem',
          alignItems: 'center',
          flexWrap: displayInOneLine ? 'nowrap' : 'wrap',
          minWidth: 0,
          ...sx,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            ...(!displayInOneLine && { alignSelf: 'flex-start' }),
            color: (theme) => theme.palette.custom.OV.sys.outline,
          }}
        >
          {props.title}
        </Typography>
        {displayInOneLine ? (
          <OverflowList<ParticipantSelectorItem>
            sx={{
              gap: '0.3rem',
              flexWrap: 'nowrap',
            }}
            items={props.participants}
            itemRenderer={renderParticipantElement}
            showMoreElement={renderShowMoreElement}
          />
        ) : (
          props.participants.map(renderParticipantElement)
        )}

        {!readOnly && !!showAddButton && (
          <Button
            disableRipple
            variant="text"
            onClick={openAddParticipantModal}
            sx={{
              minWidth: 0,
              padding: '0 0.5rem',
              fontSize: '0.75rem',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              height: '1.25rem',
            }}
          >
            {props.addParticipantLabel}
          </Button>
        )}
        {endAdornment}
      </Stack>
    </Stack>
  );
};
